import {
  Component, OnInit, Input, ElementRef, ContentChildren,
  ViewChild, ViewContainerRef, TemplateRef, DoCheck, IterableDiffers
} from '@angular/core';
import { MdSidenav } from '@angular/material';
import { CfCoreComponent } from '../core/core.component';
import { SidenavModel } from '../../models/sidenav/sidenav.model';
import { SidenavStylingModel } from '../../models/sidenav/sidenav-styling.model';
import { SidenavTemplates } from '../../templates/sidenav.template';
import { TemplateService } from '../../services/template-service/template.service';
/**
 * <p>Sidenav component based on Angular Material Sidenav</p>
 * <p>Example of using:</p>
 * <pre>
 * <code><</code>cf-sidenav [properties]="mySidenav" [styling]="mySidenavStyles"<code>></code>
 * <code><</code><code>/</code>cf-sidenav<code>></code>
 * </pre>
 */
export class CfSidenavComponent extends CfCoreComponent implements OnInit, DoCheck {
/**
 * SidenavModel object.
 * <p>Example of model:</p>
 * <code><pre>{{'{'}}
 *  contentId: 'mySidenavContent',
 *  leftSideId: 'mySidenavLeftSide',
 *  rightSideId: 'mySidenavRightSide',
 *  leftSideMode: 'push',
 *  rightSideMode: 'side',
 *  fullHeight: true,
 *  leftSideWidth: '35%',
 *  rightSideWidth: '300px',
 * {{'}'}}</pre></code>
 * <h4>contentId: string</h4>
 * <b>ng-template id (#)</b> inside component tags to be used as html for sidenav content section
 * <h4>leftSideId: string</h4>
 * <b>ng-template id (#)</b> inside component tags to be used as html for sidenav left side
 * <h4>rightSideId: string</h4>
 * <b>ng-template id (#)</b> inside component tags to be used as html for sidenav right side
 * <h4>leftSideMode: string</h4>
 * Opening mode. Can be: over, push, side. Default: over
 * <h4>rightSideMode: string</h4>
 * Opening mode. Can be: over, push, side. Default: over
 * <h4>fullHeight: boolean</h4>
 * If the sidenav must take height of parent container. Default: false
 * <h4>leftSideWidth: string</h4>
 * Width of left side. Not specified by default and will adjust to fit content
 * <h4>rightSideWidth: string</h4>
 * Width of right side. Not specified by default and will adjust to fit content
 */
public properties: SidenavModel;
/**
 * Object for all styling properties:
 * <h4>container: StylingModel</h4>
 * Styling properties for main sidenav container
 * <h4>content: StylingModel</h4>
 * Styling properties for sidenav content section
 * <h4>leftSide: StylingModel</h4>
 * Styling properties for sidenav leftSide section
 * <h4>rightSide: StylingModel</h4>
 * Styling properties for sidenav rightSide section
 */
public styling: SidenavStylingModel;
/**
 * <b>ng-template id (#)</b> inside component tags to be used as html for sidenav content section.
 * @param {?} value
 * @return {?}
 */
set contentId(value: string) { this.properties.contentId = value; }
/**
 * @hidden
 */
_contentIdIndex: number;
/**
 * <b>ng-template id (#)</b> inside component tags to be used as html for sidenav left side.
 * @param {?} value
 * @return {?}
 */
set leftSideId(value: string) { this.properties.leftSideId = value; }
/**
 * @hidden
 */
_leftSideIdIndex: number;
/**
 * <b>ng-template id (#)</b> inside component tags to be used as html for sidenav right side.
 * @param {?} value
 * @return {?}
 */
set rightSideId(value: string) { this.properties.rightSideId = value; }
/**
 * @hidden
 */
_rightSideIdIndex: number;
/**
 * Opening mode. Can be: over, push, side. Default: over.
 * @param {?} value
 * @return {?}
 */
set leftSideMode(value: string) { this.properties.leftSideMode = value; }
/**
 * Opening mode. Can be: over, push, side. Default: over.
 * @param {?} value
 * @return {?}
 */
set rightSideMode(value: string) { this.properties.rightSideMode = value; }
/**
 * If the sidenav must take height of parent container. Default: false.
 * @param {?} value
 * @return {?}
 */
set fullHeight(value: boolean) { this.properties.fullHeight = value; }
/**
 * Width of left side. Not specified by default and will adjust to fit content.
 * @param {?} value
 * @return {?}
 */
set leftSideWidth(value: string) { this.properties.leftSideWidth = value; }
/**
 * Width of right side. Not specified by default and will adjust to fit content.
 * @param {?} value
 * @return {?}
 */
set rightSideWidth(value: string) { this.properties.rightSideWidth = value; }
/**
 * @hidden
 */
cfSidenavTemplates;
/**
 * @hidden
 */
sidenavElement;
/**
 * @hidden
 */
leftSide: MdSidenav;
/**
 * @hidden
 */
sidenavContent: ViewContainerRef;
/**
 * @hidden
 */
rightSide: MdSidenav;
/**
 * @hidden
 */
differ: any;
/**
 * @hidden
 */
_templatesChecked;
/**
 * @hidden
 * @param {?} elementRef
 * @param {?} templateService
 * @param {?} differs
 */
constructor(public elementRef: ElementRef,
public templateService: TemplateService, /**@hidden */differs: IterableDiffers) {
    super(elementRef, templateService);
    this.differ = differs.find([]).create(null);
    this.properties = new SidenavModel();
    this.styling = new SidenavStylingModel();
  }
/**
 * @hidden 
 * Init all component data
 * @return {?}
 */
ngOnInit() {
    this.getMyTemplate('sidenav', SidenavTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'SIDENAV').then( sampler => {
        // console.log('SIDENAV', this);
      });
    });
  }
/**
 * @hidden
 * @return {?}
 */
ngDoCheck() {
    if (!this._templatesChecked && this.properties != null &&
      (this.properties.contentId !== '' || this.properties.leftSideId !== '' || this.properties.rightSideId !== '')) {
      let /** @type {?} */ changes = this.differ.diff(this.cfSidenavTemplates);
      if (changes) {
        changes.collection._results.forEach((tmpl, index) => {
          let /** @type {?} */ tmplKey = Object.keys(tmpl._def.references)[0];
          if (this.properties.contentId === tmplKey) { this._contentIdIndex = index; }
          if (this.properties.leftSideId === tmplKey) { this._leftSideIdIndex = index; }
          if (this.properties.rightSideId === tmplKey) { this._rightSideIdIndex = index; }
        });
        this._templatesChecked = true;
      }
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-sidenav',
  template: "<div *ngIf=\"_inited && properties?.display\" class=\"{{activeTemplate.style?.container?.class}} {{styling?.container?.class}} {{getDynamicClasses(activeTemplate.style?.container?.dynamicClass)}} {{getDynamicClasses(styling?.container?.dynamicClass)}} {{properties?.fullHeight===true ? 'full-height' : ''}}\" [ngClass]=\"'cf-sidenav-container'\"> <md-sidenav-container #sidenavElement> <md-sidenav #leftSide *ngIf=\"properties?.leftSideId !== ''\" align=\"start\" mode=\"{{properties?.leftSideMode}}\" [style.width]=\"properties?.leftSideWidth\" class=\"{{activeTemplate.style?.leftSide?.class}} {{styling?.leftSide?.class}} {{getDynamicClasses(activeTemplate.style?.leftSide?.dynamicClass)}} {{getDynamicClasses(styling?.leftSide?.dynamicClass)}}\" [ngClass]=\"'cf-sidenav-left'\"> <div [ngTemplateOutlet]=\"cfSidenavTemplates._results[_leftSideIdIndex]\"></div> </md-sidenav> <div #sidenavContent *ngIf=\"properties?.contentId !== ''\" class=\"{{activeTemplate.style?.content?.class}} {{styling?.content?.class}} {{getDynamicClasses(activeTemplate.style?.content?.dynamicClass)}} {{getDynamicClasses(styling?.content?.dynamicClass)}}\" [ngClass]=\"'cf-sidenav-content'\"> <div [ngTemplateOutlet]=\"cfSidenavTemplates._results[_contentIdIndex]\"></div> </div> <md-sidenav #rightSide *ngIf=\"properties?.rightSideId !== ''\" align=\"end\" mode=\"{{properties?.rightSideMode}}\" [style.width]=\"properties?.rightSideWidth\" class=\"{{activeTemplate.style?.rightSide?.class}} {{styling?.rightSide?.class}} {{getDynamicClasses(activeTemplate.style?.rightSide?.dynamicClass)}} {{getDynamicClasses(styling?.rightSide?.dynamicClass)}}\" [ngClass]=\"'cf-sidenav-right'\"> <div [ngTemplateOutlet]=\"cfSidenavTemplates._results[_rightSideIdIndex]\"></div> </md-sidenav> </md-sidenav-container> </div> ",
  styles: [":host ::ng-deep .cf-sidenav-container, :host ::ng-deep .cf-sidenav-content { box-sizing: border-box; } :host ::ng-deep > .cf-sidenav-container.full-height { height: 100%; } :host ::ng-deep > .cf-sidenav-container.full-height > md-sidenav-container { height: 100%; } :host ::ng-deep > .cf-sidenav-container.full-height > md-sidenav-container > .mat-sidenav-content > .cf-sidenav-content { height: 100%; } :host ::ng-deep [hidden] { display: none !important; } "]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
{type: IterableDiffers, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'contentId': [{ type: Input, args: ['contentId', ] },],
'leftSideId': [{ type: Input, args: ['leftSideId', ] },],
'rightSideId': [{ type: Input, args: ['rightSideId', ] },],
'leftSideMode': [{ type: Input, args: ['leftSideMode', ] },],
'rightSideMode': [{ type: Input, args: ['rightSideMode', ] },],
'fullHeight': [{ type: Input, args: ['fullHeight', ] },],
'leftSideWidth': [{ type: Input, args: ['leftSideWidth', ] },],
'rightSideWidth': [{ type: Input, args: ['rightSideWidth', ] },],
'cfSidenavTemplates': [{ type: ContentChildren, args: [TemplateRef, ] },],
'sidenavElement': [{ type: ViewChild, args: ['sidenavElement', ] },],
'leftSide': [{ type: ViewChild, args: ['leftSide', { read: MdSidenav }, ] },],
'sidenavContent': [{ type: ViewChild, args: ['sidenavContent', { read: ViewContainerRef }, ] },],
'rightSide': [{ type: ViewChild, args: ['rightSide', { read: MdSidenav }, ] },],
};
}

function CfSidenavComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfSidenavComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfSidenavComponent.ctorParameters;
/** @type {?} */
CfSidenavComponent.propDecorators;
/**
 * SidenavModel object.
 * <p>Example of model:</p>
 * <code><pre>{{'{'}}
 *  contentId: 'mySidenavContent',
 *  leftSideId: 'mySidenavLeftSide',
 *  rightSideId: 'mySidenavRightSide',
 *  leftSideMode: 'push',
 *  rightSideMode: 'side',
 *  fullHeight: true,
 *  leftSideWidth: '35%',
 *  rightSideWidth: '300px',
 * {{'}'}}</pre></code>
 * <h4>contentId: string</h4>
 * <b>ng-template id (#)</b> inside component tags to be used as html for sidenav content section
 * <h4>leftSideId: string</h4>
 * <b>ng-template id (#)</b> inside component tags to be used as html for sidenav left side
 * <h4>rightSideId: string</h4>
 * <b>ng-template id (#)</b> inside component tags to be used as html for sidenav right side
 * <h4>leftSideMode: string</h4>
 * Opening mode. Can be: over, push, side. Default: over
 * <h4>rightSideMode: string</h4>
 * Opening mode. Can be: over, push, side. Default: over
 * <h4>fullHeight: boolean</h4>
 * If the sidenav must take height of parent container. Default: false
 * <h4>leftSideWidth: string</h4>
 * Width of left side. Not specified by default and will adjust to fit content
 * <h4>rightSideWidth: string</h4>
 * Width of right side. Not specified by default and will adjust to fit content
 * @type {?}
 */
CfSidenavComponent.prototype.properties;
/**
 * Object for all styling properties:
 * <h4>container: StylingModel</h4>
 * Styling properties for main sidenav container
 * <h4>content: StylingModel</h4>
 * Styling properties for sidenav content section
 * <h4>leftSide: StylingModel</h4>
 * Styling properties for sidenav leftSide section
 * <h4>rightSide: StylingModel</h4>
 * Styling properties for sidenav rightSide section
 * @type {?}
 */
CfSidenavComponent.prototype.styling;
/**
 * @hidden
 * @type {?}
 */
CfSidenavComponent.prototype._contentIdIndex;
/**
 * @hidden
 * @type {?}
 */
CfSidenavComponent.prototype._leftSideIdIndex;
/**
 * @hidden
 * @type {?}
 */
CfSidenavComponent.prototype._rightSideIdIndex;
/**
 * @hidden
 * @type {?}
 */
CfSidenavComponent.prototype.cfSidenavTemplates;
/**
 * @hidden
 * @type {?}
 */
CfSidenavComponent.prototype.sidenavElement;
/**
 * @hidden
 * @type {?}
 */
CfSidenavComponent.prototype.leftSide;
/**
 * @hidden
 * @type {?}
 */
CfSidenavComponent.prototype.sidenavContent;
/**
 * @hidden
 * @type {?}
 */
CfSidenavComponent.prototype.rightSide;
/**
 * @hidden
 * @type {?}
 */
CfSidenavComponent.prototype.differ;
/**
 * @hidden
 * @type {?}
 */
CfSidenavComponent.prototype._templatesChecked;
/** @type {?} */
CfSidenavComponent.prototype.elementRef;
/** @type {?} */
CfSidenavComponent.prototype.templateService;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
